import { SearchSideBarLayout } from 'components/SearchSideBar/SearchSidebarLayout';
import { FC } from 'react';
import dynamic from 'next/dynamic';
import { SearchKeyword } from 'components/SearchSideBar/SearchKeyword';
import { OverlaySearchArea } from 'components/SearchSideBar/OverlaySearchArea';
import { SearchButtonArea } from 'components/SearchSideBar/SearchButtonArea';
import { useSearchCondition } from 'components/SearchSideBar/hooks/useSearchCondition';
import { useShowOverlay } from 'components/SearchSideBar/hooks/useShowOverlay';
import { BasicInfo } from 'components/SearchSideBar/BasicInfo';
import { PickUpConditions } from 'components/SearchSideBar/PickUpCondition';
import { Details } from 'components/SearchSideBar/Details';
import { useCountUpdateAnimation } from 'hooks/useCountUpdateAnimation';
import { getOldestDayWdtno } from 'components/SearchSideBar/logics/getOldestDayWdtno';
import { ModalOverlay } from 'components/ModalOverlay';
import { useDisclosure } from '@doda/common/src/hooks/useDisclosure';
import { ModalMasterData } from 'components/SearchSideBar/BasicInfo/types';
import { createOccupationDisplayData } from 'components/OccupationSelectModal/logics/createOccupationDisplayData';
import {
  createDisplayArea,
  createDisplayCity,
} from 'components/LocationStationModal/LocationModal/logics/createLocationDisplayData';
import { createDisplayStation } from 'components/LocationStationModal/StationModal/logics/createStationDisplayData';
import { createIndustryDisplayData } from 'components/IndustryModal/logics/createIndustryDisplayData';
import { useEachModalJobCount } from 'hooks/useEachModalJobCount';
import { useSearch } from './hooks/useSearch';
import { CurrentCondition } from '../CurrentCondition';
import { SideBarProps } from './types';

const OccupationModal = dynamic(
  () =>
    import('components/OccupationSelectModal').then((module) => ({
      default: module.OccupationModal,
    })),
  { ssr: false }
);
const LocationModal = dynamic(
  () =>
    import('components/LocationStationModal/LocationModal').then((module) => ({
      default: module.LocationModal,
    })),
  { ssr: false }
);
const StationModal = dynamic(
  () =>
    import('components/LocationStationModal/StationModal').then((module) => ({
      default: module.StationModal,
    })),
  { ssr: false }
);
const IndustryModal = dynamic(
  () =>
    import('components/IndustryModal').then((module) => ({
      default: module.IndustryModal,
    })),
  { ssr: false }
);

/**
 * Propsの定義
 */
interface Props {
  sideBarProps: SideBarProps;
  searchConditionLabel: string | undefined;
  modalMasterData: ModalMasterData;
}

export const SearchSidebar: FC<Props> = ({
  sideBarProps,
  searchConditionLabel,
  modalMasterData,
}) => {
  const { savedSearches, initialSideBarMasterData, initialJobCountData } =
    sideBarProps;
  // FIXME: URLのoldestDayWdtnoパラメータが不要か確認して、不要であれば削除
  const oldestDayWdtno = getOldestDayWdtno(savedSearches);

  const {
    occupation,
    location,
    routeStation,
    industry,
    employmentStatus,
    income,
    includeSearchKeyword,
    excludeSearchKeyword,
    pickUpCondition,
    searchDetail,
    updateJobCount,
    totalCount,
    selectedCurrentCondition,
    modalTotalCount,
    updateModalJobCount,
    isConditionUpperLimitError,
  } = useSearchCondition({
    initialSideBarMasterData,
    initialJobCountData,
  });

  const {
    onClickSearchButton,
    onClickOccupationModalSearchButton,
    onClickLocationModalSearchButton,
    onClickRouteStationModalSearchButton,
    onClickIndustryModalSearchButton,
  } = useSearch(selectedCurrentCondition, oldestDayWdtno);
  const { isOpacity, isInvisible, onChangeIsShowOverlay, observerRef } =
    useShowOverlay();
  const countAnimationRef = useCountUpdateAnimation(totalCount);

  const occupationModal = useDisclosure();
  const industryModal = useDisclosure();
  const locationModal = useDisclosure();
  const stationModal = useDisclosure();

  const {
    occupationModalJobCount,
    locationAndStationModalJobCount,
    industryModalJobCount,
    getJobCountWithoutOccupation,
    getJobCountWithoutLocationAndStation,
    getJobCountWithoutIndustry,
  } = useEachModalJobCount(selectedCurrentCondition);

  return (
    <>
      <SearchSideBarLayout>
        <CurrentCondition searchConditionLabel={searchConditionLabel} />
        <BasicInfo
          conditions={{
            occupation,
            location,
            routeStation,
            industry,
            employmentStatus,
            income,
          }}
          modalAction={{
            onOpenOccupationModal: occupationModal.onOpen,
            onOpenLocationModal: locationModal.onOpen,
            onOpenRouteModal: stationModal.onOpen,
            onOpenIndustryModal: industryModal.onOpen,
          }}
          getJobCountAtModalDisplay={{
            getJobCountWithoutOccupation,
            getJobCountWithoutLocationAndStation,
            getJobCountWithoutIndustry,
          }}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <PickUpConditions
          list={pickUpCondition.pickUpConditionDisplayList}
          updateIsCheckedIsNewArrival={
            pickUpCondition.updateIsCheckedIsNewArrival
          }
          updateIsCheckedIsNearDeadline={
            pickUpCondition.updateIsCheckedIsNearDeadline
          }
          updateIsCheckedHolidayAndWayOfWorkingList={
            searchDetail.holidayAndWayOfWorking
              .updateIsCheckedHolidayAndWayOfWorkingList
          }
          updateIsCheckedRequirementList={
            searchDetail.requirements.updateIsCheckedRequirementList
          }
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <SearchKeyword
          include={includeSearchKeyword}
          exclude={excludeSearchKeyword}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <Details
          searchDetail={searchDetail}
          updateJobCount={updateJobCount}
          onChangeIsShowOverlay={onChangeIsShowOverlay}
        />
        <SearchButtonArea
          onClickSearchButton={onClickSearchButton}
          observerRef={observerRef}
          countAnimationRef={countAnimationRef}
          validationFlags={{
            isIncomeRangeError: income.isIncomeRangeError,
            isConditionUpperLimitError,
          }}
        />
        <OverlaySearchArea
          totalCount={totalCount}
          isOpacity={isOpacity}
          isInvisible={isInvisible}
          onClickSearchButton={onClickSearchButton}
          validationFlags={{
            isIncomeRangeError: income.isIncomeRangeError,
            isConditionUpperLimitError,
          }}
        />
      </SearchSideBarLayout>
      <ModalOverlay
        show={
          occupationModal.isOpen ||
          locationModal.isOpen ||
          stationModal.isOpen ||
          industryModal.isOpen
        }
        onClickOverlay={() => {
          occupationModal.onClose();
          locationModal.onClose();
          stationModal.onClose();
          industryModal.onClose();
        }}
      >
        {occupationModal.isOpen && (
          <OccupationModal
            show={occupationModal.isOpen}
            onClickClose={occupationModal.onClose}
            onClickSearch={(checkedState) => {
              onClickOccupationModalSearchButton({
                searchJobTypeLId: checkedState.L,
                searchJobTypeMId: checkedState.M,
                searchJobTypeSId: checkedState.S,
              });
            }}
            onChangeSelectedOccupation={(checkedState) => {
              updateModalJobCount({
                searchJobTypeLId: checkedState.L,
                searchJobTypeMId: checkedState.M,
                searchJobTypeSId: checkedState.S,
              });
            }}
            displayOccupationData={createOccupationDisplayData(
              modalMasterData.occupation,
              occupationModalJobCount
            )}
            selectedOccupationIdList={{
              L: selectedCurrentCondition.searchJobTypeLId,
              M: selectedCurrentCondition.searchJobTypeMId,
              S: selectedCurrentCondition.searchJobTypeSId,
            }}
            matchJobCount={modalTotalCount}
            submitButtonText="この条件で検索する"
            validationFlags={{
              isIncomeRangeError: income.isIncomeRangeError,
              isConditionUpperLimitError,
            }}
          />
        )}
        {locationModal.isOpen && (
          <LocationModal
            show={locationModal.isOpen}
            onClickClose={locationModal.onClose}
            initialTotalCount={modalTotalCount}
            selectedLocationModalIdList={{
              checkedArea: selectedCurrentCondition.areaId,
              checkedPref: selectedCurrentCondition.prefectureId,
              checkedCity: selectedCurrentCondition.cityId,
              checkedWard: selectedCurrentCondition.wardId,
            }}
            areaList={createDisplayArea(
              modalMasterData.area,
              locationAndStationModalJobCount
            )}
            cityList={createDisplayCity(
              modalMasterData.area,
              locationAndStationModalJobCount
            )}
            onClickSearch={(checkedState) => {
              onClickLocationModalSearchButton({
                areaId: checkedState.checkedAllArea,
                prefectureId: checkedState.checkedAllPref,
                cityId: checkedState.checkedAllCity,
                wardId: checkedState.checkedWard,
              });
            }}
            onChangeSelectedLocation={(checkedState) => {
              updateModalJobCount({
                areaId: checkedState.checkedAllArea,
                prefectureId: checkedState.checkedAllPref,
                cityId: checkedState.checkedAllCity,
                wardId: checkedState.checkedWard,
              });
            }}
            submitButtonText="この条件で検索する"
            validationFlags={{
              isIncomeRangeError: income.isIncomeRangeError,
              isConditionUpperLimitError,
            }}
          />
        )}
        {stationModal.isOpen && (
          <StationModal
            show={stationModal.isOpen}
            onClickClose={stationModal.onClose}
            initialTotalCount={modalTotalCount}
            selectedStationModalIdList={{
              stationList: selectedCurrentCondition.stationId,
              routeList: selectedCurrentCondition.routeId,
            }}
            areaList={createDisplayArea(
              modalMasterData.area,
              locationAndStationModalJobCount
            )}
            railwayCompanyList={createDisplayStation(
              modalMasterData.route,
              locationAndStationModalJobCount
            )}
            onClickSearch={(checkedState) => {
              onClickRouteStationModalSearchButton({
                routeId: checkedState.checkedAllRoute,
                stationId: checkedState.checkedStation,
              });
            }}
            onChangeSelectedStation={(checkedState) => {
              updateModalJobCount({
                routeId: checkedState.checkedAllRoute,
                stationId: checkedState.checkedStation,
              });
            }}
            submitButtonText="この条件で検索する"
            validationFlags={{
              isIncomeRangeError: income.isIncomeRangeError,
              isConditionUpperLimitError,
            }}
          />
        )}
        {industryModal.isOpen && (
          <IndustryModal
            show={industryModal.isOpen}
            onClickClose={industryModal.onClose}
            onClickSearch={(checkedState) => {
              onClickIndustryModalSearchButton({
                industryLId: checkedState.L,
                industrySId: checkedState.S,
              });
            }}
            onChangeSelectedIndustry={(checkedState) => {
              updateModalJobCount({
                industryLId: checkedState.L,
                industrySId: checkedState.S,
              });
            }}
            displayIndustryData={createIndustryDisplayData(
              modalMasterData.industry,
              industryModalJobCount
            )}
            selectedIndustryIdList={{
              L: selectedCurrentCondition.industryLId,
              S: selectedCurrentCondition.industrySId,
            }}
            matchJobCount={modalTotalCount}
            submitButtonText="この条件で検索する"
            validationFlags={{
              isIncomeRangeError: income.isIncomeRangeError,
              isConditionUpperLimitError,
            }}
          />
        )}
      </ModalOverlay>
    </>
  );
};
